import { annualCost, type Produktversion, type Profil, type Satser } from "@biloplader/calc";

/** Mirrors the API's ProduktDto (camelCase JSON). */
export interface ProduktDto {
  produktversionId: number;
  udbyder: string;
  udbyderSlug: string;
  udbyderKoncern: string | null;
  produkt: string;
  hjemmeside: string | null;
  gyldigFra: string;
  gyldigTil: string | null;
  prismodel: "spot" | "fast";
  fastprisKrKwh: number;
  kwhTillaeg: number;
  tillaegTaerskel: number | null;
  maanedsabonnement: number;
  refusionHaandtering: "Ingen" | "Fuld" | "Delvis";
  refusionAndel: number;
  refusionGebyr: number;
  udeprisKrKwh: number;
  udeprisSomHjemme: boolean;
  roamingRabat: number;
  ladeboksInkluderet: boolean;
  boksPris: number;
  oprettelsesGebyr: number;
  kraeverEgenBoks: boolean;
  virkerPaaKraftstik: boolean;
  offentligOpladningInkluderet: boolean;
  bindingsperiodeMdr: number;
  affiliateLink: string | null;
  fremhaevetLink: boolean;
  vilkaar: { sprog: string; resume: string; fritekstMarkdown: string } | null;
}

/** Mirrors the API's RatesDto. */
export interface RatesDto {
  gaelderDato: string;
  area: string;
  elafgift: number;
  lovligRefusionssats: number;
  standardNettarif: number;
  standardNettarifNat: number;
  spotpris: number;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** GET JSON, retrying only on 5xx / network errors — the serverless DB can cold-start.
 * 4xx fails fast (the throw is outside the retry try/catch so it isn't swallowed). */
async function getJson<T>(url: string, tries = 3): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < tries; i++) {
    let res: Response | null = null;
    try {
      res = await fetch(url, { headers: { Accept: "application/json" } });
    } catch (e) {
      lastErr = e; // network error → retriable
    }
    if (res) {
      if (res.ok) return (await res.json()) as T;
      if (res.status < 500) throw new Error(`${res.status} ${res.statusText}`); // fail fast
      lastErr = new Error(`${res.status} ${res.statusText}`);
    }
    if (i < tries - 1) await sleep(1500 * (i + 1));
  }
  throw lastErr;
}

/** In-flight/recent GETs by URL. The data changes rarely (rates are daily), so a
 * short TTL makes revisits, back-navigation and toggling back and forth instant.
 * A rejected promise is dropped so a failure isn't cached. */
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; p: Promise<unknown> }>();

function cachedGet<T>(url: string): Promise<T> {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.p as Promise<T>;
  const p = getJson<T>(url).catch((e) => {
    cache.delete(url);
    throw e;
  });
  cache.set(url, { at: Date.now(), p });
  return p;
}

const ratesUrl = (lang: string, date?: string, area = "DK1") =>
  `/api/rates?lang=${lang}&area=${area}${date ? `&date=${date}` : ""}`;

const productsUrl = (lang: string, date?: string, includeExpired = false) =>
  `/api/products?lang=${lang}${date ? `&date=${date}` : ""}${includeExpired ? "&includeExpired=true" : ""}`;

export const fetchRates = (lang: string, date?: string, area = "DK1") =>
  cachedGet<RatesDto>(ratesUrl(lang, date, area));

export const fetchProducts = (lang: string, date?: string, includeExpired = false) =>
  cachedGet<ProduktDto[]>(productsUrl(lang, date, includeExpired));

/** Warm the two calls the results step makes for the default wizard state, while
 * the user is still filling in the form. Also wakes the serverless API + DB, so
 * a cold start is paid during the wizard instead of in front of a spinner.
 * Errors are ignored here — Results refetches and handles them. */
export function prefetchDefaults(lang: string): void {
  void fetchProducts(lang).catch(() => {});
  void fetchRates(lang).catch(() => {});
}

/** The provider link for a product — the direct product page when we have it. */
export const produktLink = (p: ProduktDto): string | null => p.affiliateLink ?? p.hjemmeside;

/** A version is expired relative to the comparison date (default today). */
export function erUdloebet(p: ProduktDto, date?: string): boolean {
  const d = date ?? new Date().toISOString().slice(0, 10);
  return p.gyldigTil !== null && p.gyldigTil <= d;
}

export type FejlKategori = "LinkVirkerIkke" | "LinkForkertProdukt" | "PrisVilkaarForkert" | "Andet";

/** Submit an anonymous error report for a product; resolves true on success. */
export async function postFejl(produktversionId: number, kategori: FejlKategori, kommentar: string): Promise<boolean> {
  try {
    const res = await fetch("/api/fejl", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ produktversionId, kategori, kommentar: kommentar || null }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Adapter: API rates → pure-engine Satser. */
export function ratesToSatser(r: RatesDto): Satser {
  return {
    elafgift: r.elafgift,
    lovligRefusionssats: r.lovligRefusionssats,
    standardNettarif: r.standardNettarif,
    standardNettarifNat: r.standardNettarifNat,
    spotpris: r.spotpris,
  };
}

/** Adapter: API product → pure-engine Produktversion (derives the effective refund share). */
export function productToPv(p: ProduktDto): Produktversion {
  const refusionAndel =
    p.refusionHaandtering === "Fuld" ? 1 : p.refusionHaandtering === "Ingen" ? 0 : p.refusionAndel;
  return {
    prismodel: p.prismodel,
    fastprisKrKwh: p.fastprisKrKwh,
    kwhTillaeg: p.kwhTillaeg,
    tillaegTaerskel: p.tillaegTaerskel,
    maanedsabonnement: p.maanedsabonnement,
    refusionAndel,
    refusionGebyr: p.refusionGebyr,
    udeprisKrKwh: p.udeprisKrKwh,
    udeprisSomHjemme: p.udeprisSomHjemme,
    ladeboksInkluderet: p.ladeboksInkluderet,
    boksPris: p.boksPris,
    oprettelsesGebyr: p.oprettelsesGebyr,
  };
}

/** A user-entered "own agreement" — compared locally and submitted as an
 * anonymous product suggestion for the backoffice. */
export interface EgenAftale {
  navn: string;
  kommentar: string;
  prismodel: "spot" | "fast";
  fastprisKrKwh: number;
  kwhTillaeg: number;
  maanedsabonnement: number;
  udeprisKrKwh: number;
  ladeboksInkluderet: boolean;
  boksPris: number;
  oprettelsesGebyr: number;
  bindingsperiodeMdr: number;
}

/** Synthetic id marking the user's own agreement in result rows. */
export const EGEN_ID = -1;

/** Adapter: own agreement → ProduktDto so it rides the normal compare pipeline. */
export function egenToDto(e: EgenAftale): ProduktDto {
  return {
    produktversionId: EGEN_ID,
    udbyder: e.navn,
    udbyderSlug: "egen",
    udbyderKoncern: null,
    produkt: "",
    hjemmeside: null,
    gyldigFra: "",
    gyldigTil: null,
    prismodel: e.prismodel,
    fastprisKrKwh: e.fastprisKrKwh,
    kwhTillaeg: e.kwhTillaeg,
    tillaegTaerskel: null,
    maanedsabonnement: e.maanedsabonnement,
    refusionHaandtering: "Ingen",
    refusionAndel: 0,
    refusionGebyr: 0,
    udeprisKrKwh: e.udeprisKrKwh,
    udeprisSomHjemme: false,
    roamingRabat: 0,
    ladeboksInkluderet: e.ladeboksInkluderet,
    boksPris: e.boksPris,
    oprettelsesGebyr: e.oprettelsesGebyr,
    kraeverEgenBoks: false,
    virkerPaaKraftstik: true,
    offentligOpladningInkluderet: false,
    bindingsperiodeMdr: e.bindingsperiodeMdr,
    affiliateLink: null,
    fremhaevetLink: false,
    vilkaar: null,
  };
}

/** Extras submitted with an own agreement but never kept in localStorage:
 * the user's pasted terms and any attached files. */
export interface EgenAftaleVedhaeft {
  vilkaarTekst: string;
  filer: File[];
}

/** Attachment limits — mirrored server-side in ForslagFunctions. */
export const BILAG_MAKS_ANTAL = 5;
export const BILAG_MAKS_BYTES = 5 * 1024 * 1024;
export const BILAG_TYPER = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.txt,.md,.csv,.doc,.docx";

/** Submit the own agreement as an anonymous suggestion, with the user's terms
 * and any attached files. Resolves false if the submission didn't land — the
 * caller surfaces that only when the user actually attached something. */
export async function postForslag(e: EgenAftale, v?: EgenAftaleVedhaeft): Promise<boolean> {
  const data = {
    navn: e.navn,
    kommentar: e.kommentar || null,
    vilkaarTekst: v?.vilkaarTekst || null,
    prismodel: e.prismodel === "fast" ? "Fastpris" : "SpotPlusTillaeg",
    fastprisKrKwh: e.fastprisKrKwh,
    kwhTillaeg: e.kwhTillaeg,
    maanedsabonnement: e.maanedsabonnement,
    udeprisKrKwh: e.udeprisKrKwh,
    ladeboksInkluderet: e.ladeboksInkluderet,
    boksPris: e.boksPris,
    oprettelsesGebyr: e.oprettelsesGebyr,
    bindingsperiodeMdr: e.bindingsperiodeMdr,
  };

  const body = new FormData();
  body.append("data", JSON.stringify(data));
  for (const f of v?.filer ?? []) body.append("filer", f, f.name);

  try {
    // No Content-Type header: the browser sets the multipart boundary itself.
    const res = await fetch("/api/forslag", { method: "POST", body });
    return res.ok;
  } catch {
    return false;
  }
}

export interface Beregning {
  produkt: ProduktDto;
  total: number;
  homePrice: number;
  udePrice: number;
  effRefusion: number;
  install: number;
}

/** Run the live engine over all products for a profile — instant, no round-trip. */
export function beregnAlle(profil: Profil, produkter: ProduktDto[], satser: Satser): Beregning[] {
  return produkter
    .map((produkt) => {
      const r = annualCost(profil, productToPv(produkt), satser);
      return { produkt, total: r.total, homePrice: r.homePrice, udePrice: r.udePrice, effRefusion: r.effRefusion, install: r.install };
    })
    .sort((a, b) => a.total - b.total);
}
